import React, { useRef, useState } from "react";
import { uploadPublicAsset } from "@/lib/upload";
import { CheckCircle, UploadSimple, CircleNotch, ArrowLeft, ArrowRight, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

export const STEPS = [
  "Verify Company", "Verify Phone", "Company Info", "Documents", "Live Selfie",
  "Driver Info", "Plans & Pricing", "Add-On Services", "Payment Authorization", "Agreement",
];

export const ELD_PROVIDERS = [
  "Motive (KeepTruckin)", "Samsara", "Geotab", "Omnitracs", "PeopleNet (Trimble)", "BigRoad", "Verizon Connect",
  "J.J. Keller Encompass", "EROAD", "Garmin eLog", "Rand McNally", "Teletrac Navman", "Blue Ink Tech", "Gorilla Safety",
  "TT ELD", "Zonar", "Apollo ELD", "Other",
];

export const inputCls =
  "w-full h-11 rounded-xl bg-white/[0.04] border border-white/10 px-3.5 text-[15px] text-white placeholder:text-white/30 outline-none focus:border-sky-400/70 focus:bg-white/[0.07] transition-colors";

export function OField({ label, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-white/40 mt-1">{hint}</span>}
    </label>
  );
}

export function StepTitle({ kicker, title, sub }) {
  return (
    <div className="mb-8">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-sky-400">{kicker}</p>
      <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white mt-2 tracking-tight">{title}</h2>
      {sub && <p className="text-white/55 mt-2 max-w-2xl leading-relaxed">{sub}</p>}
    </div>
  );
}

export function NavButtons({ onBack, onNext, nextLabel = "Continue", loading, disabled, testid = "ob-next" }) {
  return (
    <div className="mt-10 flex items-center justify-between gap-3">
      {onBack ? (
        <button onClick={onBack} className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors" data-testid="ob-back">
          <ArrowLeft size={16} />Back
        </button>
      ) : <span />}
      <button onClick={onNext} disabled={loading || disabled} data-testid={testid}
        className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-semibold shadow-[0_10px_40px_-10px_rgba(14,165,233,.7)] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-[filter,opacity]">
        {loading ? <CircleNotch size={18} className="animate-spin" /> : null}{nextLabel}{!loading && <ArrowRight size={16} weight="bold" />}
      </button>
    </div>
  );
}

export function UploadSlot({ token, label, value, onChange, accept = "*/*", testid, capture }) {
  const ref = useRef(null);
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      const asset = await uploadPublicAsset(file, token, "carrier_onboarding", setPct);
      onChange({ url: asset.url, name: file.name });
    } catch (err) { toast.error(err.response?.data?.detail || (err.message?.includes("too large") ? err.message : "Upload failed. Please check your connection and try again.")); }
    finally { setPct(null); }
  };
  const done = !!value;
  return (
    <button type="button" onClick={() => ref.current?.click()} data-testid={testid}
      className={`group relative w-full text-left rounded-2xl border p-4 transition-colors ${done ? "border-emerald-400/40 bg-emerald-400/[0.06]" : "border-dashed border-white/15 bg-white/[0.02] hover:border-sky-400/50 hover:bg-white/[0.04]"}`}>
      <input ref={ref} type="file" accept={accept} capture={capture} className="hidden" onChange={pick} />
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${done ? "bg-emerald-400/15 text-emerald-300" : "bg-white/5 text-white/50 group-hover:text-sky-300"}`}>
          {pct !== null ? <CircleNotch size={20} className="animate-spin" /> : done ? <CheckCircle size={22} weight="fill" /> : <UploadSimple size={20} />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">{label}</p>
          <p className="text-xs text-white/45 truncate flex items-center gap-1">
            {pct !== null ? `Uploading… ${pct}%` : done ? <><FileText size={12} />{value.name || "Uploaded"} · Tap to replace</> : "Tap to upload (PDF, JPG, PNG, any file · max 25MB)"}
          </p>
        </div>
      </div>
    </button>
  );
}

export const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
